import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, ArrowRight, ShieldCheck, MessageCircle, Calculator,
  MessageSquareHeart, BookOpen, Building2,
} from 'lucide-react';

const STORAGE_KEY = 'cotizacion_popup_dismissed_until';
const SHOW_DELAY_MS = 12000; // 12s
const DISMISS_HOURS = 24;

// Opciones del menú interactivo — orden por prioridad de conversión
const OPTIONS = [
  {
    id: 'cotizar',
    to: '/cotizar',
    title: 'Cotizar tratamiento gratis',
    desc: 'Recibe propuestas de centros que se ajustan a tu caso en <24h.',
    icon: MessageCircle,
    primary: true,
    badge: 'Recomendado',
  },
  {
    id: 'calculadora',
    to: '/calculadora',
    title: 'Calculadora de costos',
    desc: 'Estima cuánto cuesta un tratamiento según modalidad y duración.',
    icon: Calculator,
  },
  {
    id: 'terapeutia',
    to: '/terapeutia',
    title: 'TerapeutIA · Chat IA',
    desc: 'Habla con una IA especializada solo en adicciones. Anónimo.',
    icon: MessageSquareHeart,
    badge: 'Nuevo',
  },
  {
    id: 'educacion',
    to: '/adicciones',
    title: 'Educación sobre adicciones',
    desc: 'Aprende sobre tipos de adicción, tratamientos y recaída.',
    icon: BookOpen,
  },
  {
    id: 'centros',
    to: '/centros-de-rehabilitacion-drogas-chile',
    title: 'Ver lista de centros',
    desc: '+130 centros verificados en todas las regiones de Chile.',
    icon: Building2,
  },
];

export function CotizacionPromoPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
      if (until && Date.now() < until) return;
    } catch (e) { /* ignore */ }

    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + DISMISS_HOURS * 3600 * 1000));
    } catch (e) { /* ignore */ }
    setOpen(false);
  };

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape key closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const primary = OPTIONS.find((o) => o.primary);
  const secondary = OPTIONS.filter((o) => !o.primary);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={close}
      data-testid="cotizacion-promo-popup"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-2xl bg-slate-900 sm:rounded-2xl rounded-t-2xl border-t sm:border border-emerald-500/30 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-400"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-popup-title"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={close}
          aria-label="Cerrar"
          data-testid="cotizacion-promo-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-slate-950/80 hover:bg-slate-950 text-slate-300 hover:text-white flex items-center justify-center transition-colors backdrop-blur-sm"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8 max-h-[88vh] overflow-y-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold mb-3">
              <ShieldCheck className="w-3 h-3" /> Gratis · Confidencial
            </div>
            <h2
              id="promo-popup-title"
              className="text-2xl sm:text-3xl font-bold text-slate-50 leading-tight mb-2"
            >
              ¿En qué te <span className="text-emerald-400">podemos ayudar</span>?
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              Elige el camino que mejor se adapte a lo que estás buscando. Todo gratis, todo anónimo.
            </p>
          </div>

          {/* Primary option — grande */}
          {primary && (
            <Link
              to={primary.to}
              onClick={close}
              data-testid={`promo-option-${primary.id}`}
              className="group block bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-slate-900 hover:from-emerald-500/25 border border-emerald-500/40 hover:border-emerald-400/70 rounded-xl p-5 mb-3 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/30 transition-colors">
                  <primary.icon className="w-7 h-7 text-emerald-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-base sm:text-lg font-bold text-slate-50 leading-tight">{primary.title}</h3>
                    {primary.badge && (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-400 text-slate-950 uppercase tracking-wider">{primary.badge}</span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400 leading-snug">{primary.desc}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-emerald-400 shrink-0 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          )}

          {/* Secondary options — grid */}
          <div className="grid sm:grid-cols-2 gap-2.5">
            {secondary.map((opt) => (
              <Link
                key={opt.id}
                to={opt.to}
                onClick={close}
                data-testid={`promo-option-${opt.id}`}
                className="group flex items-start gap-3 bg-slate-950/60 hover:bg-slate-950 border border-slate-800 hover:border-emerald-500/40 rounded-xl p-3.5 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/15 transition-colors">
                  <opt.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                    <h4 className="text-sm font-semibold text-slate-50 leading-tight">{opt.title}</h4>
                    {opt.badge && (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-teal-400 text-slate-950 uppercase tracking-wider">{opt.badge}</span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug line-clamp-2">{opt.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Dismiss link */}
          <button
            type="button"
            onClick={close}
            className="mt-5 block mx-auto text-xs text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="cotizacion-promo-dismiss"
          >
            Ahora no, gracias
          </button>
        </div>
      </div>
    </div>
  );
}

export default CotizacionPromoPopup;
